import axios, { AxiosHeaders } from "axios";
import {
  clearStoredAuthToken,
  getStoredAuthToken,
  resolveAuthToken,
  storeAuthToken,
} from "@/modules/auth/utils/authToken";
import { resolveApiBaseUrl } from "@/services/apiBaseUrl";
import { recordServerDate } from "@/services/serverClock";

// The refresh_token cookie is single-use/rotated server-side, and the
// access token + cookie are both shared across every tab of the origin.
// Without this lock, two tabs whose access tokens expire around the same
// moment each try to redeem the same refresh token; the loser gets a 401
// and would otherwise wipe out the token the winner just stored, logging
// out a user who was actively using another tab. navigator.locks makes
// only one tab actually call the refresh endpoint at a time; the rest
// wait for it and adopt its result.
const AUTH_REFRESH_LOCK_NAME = "ijaazah-auth-refresh";

declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
    _retry?: boolean;
  }

  export interface InternalAxiosRequestConfig {
    skipAuthRefresh?: boolean;
    _retry?: boolean;
  }
}

const DEFAULT_HEADERS = {
  Accept: "application/json",
  "X-Requested-With": "XMLHttpRequest",
} as const;

// Must be an `/api/*` route (the `api` middleware group, no `EncryptCookies`)
// - the `refresh_token` cookie is set unencrypted by that same group's
// `/api/auth/login`, and a `web`-group route (e.g. the legacy `/{locale}/
// auth/refresh`) fails to decrypt it and always returns unauthenticated.
const AUTH_REFRESH_ENDPOINT =
  process.env.NEXT_PUBLIC_AUTH_REFRESH_ENDPOINT ?? "/api/auth/refresh";

let refreshAccessTokenPromise: Promise<string> | null = null;

const DEFAULT_TIMEOUT_MS = 60000;

import { reportApiErrorToRollbar } from "@/services/rollbar";

export const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: DEFAULT_HEADERS,
  timeout: DEFAULT_TIMEOUT_MS,
});

apiClient.interceptors.request.use((config) => {
  const requestId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
  config.headers = AxiosHeaders.from(config.headers);
  config.headers.set("X-Request-ID", requestId);
  (config as any).requestId = requestId;

  const token = getStoredAuthToken();
  if (!token || config.skipAuthRefresh) {
    return config;
  }

  config.headers.set("Authorization", `Bearer ${token}`);

  return config;
});

apiClient.interceptors.response.use(
  (res) => {
    recordServerDate(res.headers?.date);
    return res;
  },
  async (error) => {
    recordServerDate(error.response?.headers?.date);

    // Centralized expected-vs-unexpected policy (services/rollbar.ts) —
    // suppresses known business/client responses (400/401/403/404/409/422),
    // still reports 5xx, network failures, timeouts, and anything else.
    reportApiErrorToRollbar(error, { requestId: error.config?.requestId });

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (originalRequest?.skipAuthRefresh) {
      return Promise.reject(error);
    }

    if (!originalRequest || originalRequest._retry || !getStoredAuthToken()) {
      clearStoredAuthToken();
      return Promise.reject({ type: "UNAUTHORIZED" });
    }

    originalRequest._retry = true;

    // Snapshot the token that just failed - if another tab wins the
    // refresh race while we're waiting, we can tell its result apart from
    // "nothing changed" and adopt it instead of logging out.
    const tokenAtFailureTime = getStoredAuthToken();

    try {
      const nextAccessToken = await refreshAccessToken();

      originalRequest.headers = AxiosHeaders.from(originalRequest.headers);
      originalRequest.headers.set("Authorization", `Bearer ${nextAccessToken}`);

      return apiClient(originalRequest);
    } catch {
      const currentToken = getStoredAuthToken();
      if (currentToken && currentToken !== tokenAtFailureTime) {
        // A concurrent tab already refreshed successfully - use its token
        // rather than clearing it out from under a still-valid session.
        originalRequest.headers = AxiosHeaders.from(originalRequest.headers);
        originalRequest.headers.set("Authorization", `Bearer ${currentToken}`);
        return apiClient(originalRequest);
      }

      clearStoredAuthToken();
      return Promise.reject({ type: "UNAUTHORIZED" });
    }
  }
);

async function refreshAccessToken(): Promise<string> {
  if (!refreshAccessTokenPromise) {
    const staleToken = getStoredAuthToken();
    refreshAccessTokenPromise = coordinateAccessTokenRefresh(staleToken).finally(() => {
      refreshAccessTokenPromise = null;
    });
  }

  return refreshAccessTokenPromise;
}

async function coordinateAccessTokenRefresh(staleToken: string | null): Promise<string> {
  if (typeof navigator === "undefined" || !navigator.locks) {
    return requestAccessTokenRefresh();
  }

  return navigator.locks.request(AUTH_REFRESH_LOCK_NAME, async () => {
    // While we were waiting for the lock, another tab may have already
    // redeemed the (single-use) refresh token - adopt its result instead
    // of racing it with a second refresh call, which would fail and force
    // a logout.
    const currentToken = getStoredAuthToken();
    if (currentToken && currentToken !== staleToken) {
      return currentToken;
    }

    return requestAccessTokenRefresh();
  });
}

async function requestAccessTokenRefresh(): Promise<string> {
  const response = await axios.post(
    AUTH_REFRESH_ENDPOINT,
    {},
    {
      baseURL: resolveApiBaseUrl(),
      withCredentials: true,
      headers: DEFAULT_HEADERS,
      skipAuthRefresh: true,
      timeout: DEFAULT_TIMEOUT_MS,
    }
  );

  const nextAccessToken = resolveAuthToken(
    response.data,
    response.headers as Record<string, unknown>
  );

  if (!nextAccessToken) {
    throw new Error("Authentication token missing from refresh response");
  }

  const wasRemembered = typeof window !== "undefined" && window.localStorage.getItem("auth-remember") === "true";
  storeAuthToken(nextAccessToken, wasRemembered);

  return nextAccessToken;
}

export async function initializeAuthFromSession(): Promise<string | null> {
  if (getStoredAuthToken()) {
    return getStoredAuthToken();
  }
  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}
